import type { ISerialize } from '@/objects/ISerialize';
import { Category } from './Category';
import { CategoryGroupMap } from './CategoryGroupMap';
import { RaceContestantDetails } from './RaceContestantDetails';
import { EventinfoBase } from './EventinfoBase';
import { EventSetup } from './EventSetup';
import { Group } from './Group';
import { ImportFormat } from './ImportFormat';
import { Race } from './Race';
import type { BibHandleType } from './../enums/BibHandleType';

export class CreateEventRequest implements ISerialize {
    public Categories: Category[];

    public CategoryGroupConnection: CategoryGroupMap[];

    public Contestants: RaceContestantDetails[];

    public EventInfo: EventinfoBase | null;

    public EventSetup: EventSetup | null;

    public Groups: Group[];

    public GroupToRaceOneToOne: boolean;

    public ImportFormat: ImportFormat | null;

    public Races: Race[];

    public SignalrKey: string | null;

    public SourceEventBibHandleType: BibHandleType;

    public SourceEventId: number | null;

    public SourceEventIncludeAlias: boolean;

    public SourceEventIncludeContestants: boolean;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Categories?: Category[];
        CategoryGroupConnection?: CategoryGroupMap[];
        Contestants?: RaceContestantDetails[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        Groups?: Group[];
        GroupToRaceOneToOne?: boolean;
        ImportFormat?: ImportFormat | null;
        Races?: Race[];
        SignalrKey?: string | null;
        SourceEventBibHandleType?: BibHandleType;
        SourceEventId?: number | null;
        SourceEventIncludeAlias?: boolean;
        SourceEventIncludeContestants?: boolean;
    }) {
        this.Categories = values.Categories ?? [];

        this.CategoryGroupConnection = values.CategoryGroupConnection ?? [];

        this.Contestants = values.Contestants ?? [];

        this.EventInfo = values.EventInfo ?? null;

        this.EventSetup = values.EventSetup ?? null;

        this.Groups = values.Groups ?? [];

        this.GroupToRaceOneToOne = values.GroupToRaceOneToOne ?? false;

        this.ImportFormat = values.ImportFormat ?? null;

        this.Races = values.Races ?? [];

        this.SignalrKey = values.SignalrKey ?? null;

        this.SourceEventBibHandleType = values.SourceEventBibHandleType ?? 'None';

        this.SourceEventId = values.SourceEventId ?? null;

        this.SourceEventIncludeAlias = values.SourceEventIncludeAlias ?? false;

        this.SourceEventIncludeContestants = values.SourceEventIncludeContestants ?? false;
    }

    public serialize(): string {
        return (
            '{' +
            `"Categories": [ ${this.Categories.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"CategoryGroupConnection": [ ${this.CategoryGroupConnection.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Contestants": [ ${this.Contestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EventInfo": ${!this.EventInfo ? null : this.EventInfo?.serialize()}` +
            ',' +
            `"EventSetup": ${!this.EventSetup ? null : this.EventSetup?.serialize()}` +
            ',' +
            `"Groups": [ ${this.Groups.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"GroupToRaceOneToOne": ${JSON.stringify(this.GroupToRaceOneToOne)}` +
            ',' +
            `"ImportFormat": ${!this.ImportFormat ? null : this.ImportFormat?.serialize()}` +
            ',' +
            `"Races": [ ${this.Races.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"SignalrKey": ${JSON.stringify(this.SignalrKey)}` +
            ',' +
            `"SourceEventBibHandleType": ${this.SourceEventBibHandleType == 'None' ? 0 : this.SourceEventBibHandleType == 'MoveAll' ? 1 : this.SourceEventBibHandleType == 'KeepChip' ? 2 : this.SourceEventBibHandleType == 'KeepDevice' ? 3 : this.SourceEventBibHandleType == 'KeepAll' ? 4 : 0}` +
            ',' +
            `"SourceEventId": ${JSON.stringify(this.SourceEventId)}` +
            ',' +
            `"SourceEventIncludeAlias": ${JSON.stringify(this.SourceEventIncludeAlias)}` +
            ',' +
            `"SourceEventIncludeContestants": ${JSON.stringify(this.SourceEventIncludeContestants)}` +
            '}'
        );
    }

    /**
     * Creates an instance of CreateEventRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Categories?: Category[];
        CategoryGroupConnection?: CategoryGroupMap[];
        Contestants?: RaceContestantDetails[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        Groups?: Group[];
        GroupToRaceOneToOne?: boolean;
        ImportFormat?: ImportFormat | null;
        Races?: Race[];
        SignalrKey?: string | null;
        SourceEventBibHandleType?: BibHandleType;
        SourceEventId?: number | null;
        SourceEventIncludeAlias?: boolean;
        SourceEventIncludeContestants?: boolean;
    }): CreateEventRequest {
        return new CreateEventRequest(values);
    }

    public static _FromServerJson(json: any): CreateEventRequest {
        return new CreateEventRequest({
            Categories: json.Categories?.map((x: any) => Category._FromServerJson(x)),
            CategoryGroupConnection: json.CategoryGroupConnection?.map((x: any) => CategoryGroupMap._FromServerJson(x)),
            Contestants: json.Contestants?.map((x: any) => RaceContestantDetails._FromServerJson(x)),
            EventInfo: json.EventInfo ? EventinfoBase._FromServerJson(json.EventInfo) : null,
            EventSetup: json.EventSetup ? EventSetup._FromServerJson(json.EventSetup) : null,
            Groups: json.Groups?.map((x: any) => Group._FromServerJson(x)),
            GroupToRaceOneToOne: json.GroupToRaceOneToOne,
            ImportFormat: json.ImportFormat ? ImportFormat._FromServerJson(json.ImportFormat) : null,
            Races: json.Races?.map((x: any) => Race._FromServerJson(x)),
            SignalrKey: json.SignalrKey ?? '',
            SourceEventBibHandleType:
                json.SourceEventBibHandleType == 0
                    ? 'None'
                    : json.SourceEventBibHandleType == 1
                      ? 'MoveAll'
                      : json.SourceEventBibHandleType == 2
                        ? 'KeepChip'
                        : json.SourceEventBibHandleType == 3
                          ? 'KeepDevice'
                          : json.SourceEventBibHandleType == 4
                            ? 'KeepAll'
                            : 'None',
            SourceEventId: json.SourceEventId ?? null,
            SourceEventIncludeAlias: json.SourceEventIncludeAlias,
            SourceEventIncludeContestants: json.SourceEventIncludeContestants,
        });
    }

    /**
     * Creates an instance of CreateEventRequest from another instance of CreateEventRequest.
     * @param other The CreateEventRequest you want to clone
     * @returns A cloned CreateEventRequest
     */
    public static FromOther(other: CreateEventRequest): CreateEventRequest {
        return CreateEventRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return CreateEventRequest.FromOther(this);
    }

    static NAME_OF_CLASS = 'CreateEventRequest';
}
